import {
  cloneElement,
  createElement,
  lazy,
  ReactElement,
  ReactNode,
  Suspense,
  SyntheticEvent,
  useCallback,
  useInsertionEffect,
  useRef,
  type ComponentProps,
  type ComponentType,
} from 'react';
import { isElement } from 'react-is';
import { Cx } from 'tss-react';

// TODO: Investigate a stricter typing to ensure the target component supports `className`.
/**
 * Clones a React element and merges the provided className using `cx`.
 *
 * ⚠️ Note: This works even on components that don't support `className`,
 * which might lead to unexpected behavior, so try to use it only on components
 * that support `className`.
 */
export const cloneWithClassName = <T extends ReactElement<{ className?: string }>>(
  element: T,
  className: string,
  cx: Cx,
  restProps?: Partial<T['props']>,
) => cloneElement(element, { ...restProps, className: cx(element.props.className, className) });

export const tryCloneWithClassName = <T extends ReactNode>(node: T, className: string, cx: Cx) => {
  if (typeof node === 'object' && node !== null && isElement(node)) {
    return cloneElement(node as ReactElement<{ className?: string }>, {
      className: cx((node as ReactElement<{ className?: string }>).props.className, className),
    });
  }
  return node;
};

export const preloadLazy = (component: ReturnType<typeof lazy>): Promise<unknown> => {
  try {
    const { _init: initializer, _payload: payload } = component as any;
    return initializer(payload);
    // the initializer throws some promises, which can be ignored
  } catch (_ignored) {
    return Promise.resolve(null);
  }
};

/**
 * Creates a lazy component that doesn't suspend and renders null while loading.
 * @see React.lazy
 */
export const optionalLazy = <T extends ComponentType<any>>(
  factory: Parameters<typeof lazy<T>>[0],
) => {
  const LazyComponent = lazy(factory);
  const OptionalLazyComponent = (props: ComponentProps<T>) =>
    createElement(Suspense, { fallback: null }, createElement(LazyComponent, props));
  return OptionalLazyComponent;
};

export const preventDefaultEventHandler = (e: SyntheticEvent | Event) => e.preventDefault();

let lastId = 0;
/**
 * Generates a unique id.
 * @deprecated Use `useId` instead once React 18 is available.
 * @param prefix - The prefix to use for the id.
 * @returns The unique id.
 * @example
 * uniqueId() // => '1'
 * uniqueId('foo') // => 'foo2'
 */
export const uniqueId = (prefix = '') => `${prefix}${++lastId}`;

export const useRefWrapper = <T>(value: T) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

type AnyFunction = (...args: any[]) => any;
// basically the user-land implementation of `useEvent` from
// https://github.com/reactjs/rfcs/pull/220, but named differently since the RFC is being revised
export const useHandler = <T extends AnyFunction>(handler?: T | null): T => {
  const ref = useRef<T | null | undefined>(null);
  useInsertionEffect(() => {
    ref.current = handler;
  }, [handler]);

  return useCallback((...args: any[]) => ref.current?.apply(undefined, args as any), []) as T;
};
